import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK — Synthèse vocale (Text-to-Speech) Kokoro
//
// Le serveur Kokoro-FastAPI est hébergé sur 72.61.110.113:8880 et exposé
// via la fonction backend `kokoro-tts`. Le navigateur n'appelle JAMAIS l'IP
// directement : il passe par le backend, ce qui évite les problèmes de CORS
// et de contenu mixte (HTTP depuis une page HTTPS).
//
// Endpoint amont : POST /v1/audio/speech  →  audio MP3
// ─────────────────────────────────────────────────────────────

const FUNCTIONS_URL = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL as string}/functions/v1/kokoro-tts`;
const ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

/** Endpoint Kokoro exposé dans l'administration (référence documentaire). */
export const KOKORO_ENDPOINT = 'http://72.61.110.113:8880/v1/audio/speech';

/** Source de vérité : le SuperAdmin peut couper la synthèse vocale partout. */
export interface TtsConfig {
  enabled: boolean;
}

let ttsConfigCache: TtsConfig | null = null;
let ttsConfigInflight: Promise<TtsConfig> | null = null;

/** Lit l'état global de la synthèse vocale (mis en cache au niveau du module). */
export function loadTtsConfig(force = false): Promise<TtsConfig> {
  if (!force && ttsConfigCache) return Promise.resolve(ttsConfigCache);
  if (!force && ttsConfigInflight) return ttsConfigInflight;

  ttsConfigInflight = fetch(FUNCTIONS_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: ANON_KEY,
      Authorization: `Bearer ${ANON_KEY}`,
    },
    body: JSON.stringify({ action: 'config' }),
  })
    .then((r) => r.json())
    .then((data: { enabled?: boolean }) => {
      // En cas de doute on laisse la fonctionnalité active : le backend
      // refusera de toute façon les requêtes si elle est coupée.
      const cfg: TtsConfig = { enabled: data?.enabled !== false };
      ttsConfigCache = cfg;
      return cfg;
    })
    .catch(() => {
      const cfg: TtsConfig = { enabled: true };
      return cfg;
    })
    .finally(() => {
      ttsConfigInflight = null;
    });

  return ttsConfigInflight;
}

/** Force un rechargement (après un changement de réglage SuperAdmin). */
export function resetTtsConfigCache(): void {
  ttsConfigCache = null;
  ttsConfigInflight = null;
}

export interface KokoroVoice {
  id: string;
  label: string;
  lang: string;
  langLabel: string;
  gender: 'F' | 'M';
  flag: string;
}

/**
 * Catalogue des voix Kokoro-82M.
 * Le préfixe de l'identifiant donne la langue :
 * a* = anglais US, b* = anglais UK, f* = français, e* = espagnol,
 * i* = italien, h* = hindi, p* = portugais, j* = japonais, z* = chinois.
 */
export const KOKORO_VOICES: KokoroVoice[] = [
  // Français
  { id: 'ff_siwis', label: 'Siwis', lang: 'fr', langLabel: 'Français', gender: 'F', flag: '🇫🇷' },
  // Anglais (US)
  { id: 'af_heart', label: 'Heart', lang: 'en', langLabel: 'Anglais US', gender: 'F', flag: '🇺🇸' },
  { id: 'af_alloy', label: 'Alloy', lang: 'en', langLabel: 'Anglais US', gender: 'F', flag: '🇺🇸' },
  { id: 'af_aoede', label: 'Aoede', lang: 'en', langLabel: 'Anglais US', gender: 'F', flag: '🇺🇸' },
  { id: 'af_bella', label: 'Bella', lang: 'en', langLabel: 'Anglais US', gender: 'F', flag: '🇺🇸' },
  { id: 'af_jessica', label: 'Jessica', lang: 'en', langLabel: 'Anglais US', gender: 'F', flag: '🇺🇸' },
  { id: 'af_kore', label: 'Kore', lang: 'en', langLabel: 'Anglais US', gender: 'F', flag: '🇺🇸' },
  { id: 'af_nicole', label: 'Nicole', lang: 'en', langLabel: 'Anglais US', gender: 'F', flag: '🇺🇸' },
  { id: 'af_nova', label: 'Nova', lang: 'en', langLabel: 'Anglais US', gender: 'F', flag: '🇺🇸' },
  { id: 'af_river', label: 'River', lang: 'en', langLabel: 'Anglais US', gender: 'F', flag: '🇺🇸' },
  { id: 'af_sarah', label: 'Sarah', lang: 'en', langLabel: 'Anglais US', gender: 'F', flag: '🇺🇸' },
  { id: 'af_sky', label: 'Sky', lang: 'en', langLabel: 'Anglais US', gender: 'F', flag: '🇺🇸' },
  { id: 'am_adam', label: 'Adam', lang: 'en', langLabel: 'Anglais US', gender: 'M', flag: '🇺🇸' },
  { id: 'am_echo', label: 'Echo', lang: 'en', langLabel: 'Anglais US', gender: 'M', flag: '🇺🇸' },
  { id: 'am_eric', label: 'Eric', lang: 'en', langLabel: 'Anglais US', gender: 'M', flag: '🇺🇸' },
  { id: 'am_fenrir', label: 'Fenrir', lang: 'en', langLabel: 'Anglais US', gender: 'M', flag: '🇺🇸' },
  { id: 'am_liam', label: 'Liam', lang: 'en', langLabel: 'Anglais US', gender: 'M', flag: '🇺🇸' },
  { id: 'am_michael', label: 'Michael', lang: 'en', langLabel: 'Anglais US', gender: 'M', flag: '🇺🇸' },
  { id: 'am_onyx', label: 'Onyx', lang: 'en', langLabel: 'Anglais US', gender: 'M', flag: '🇺🇸' },
  { id: 'am_puck', label: 'Puck', lang: 'en', langLabel: 'Anglais US', gender: 'M', flag: '🇺🇸' },
  { id: 'am_santa', label: 'Santa', lang: 'en', langLabel: 'Anglais US', gender: 'M', flag: '🇺🇸' },
  // Anglais (UK)
  { id: 'bf_alice', label: 'Alice', lang: 'en-gb', langLabel: 'Anglais UK', gender: 'F', flag: '🇬🇧' },
  { id: 'bf_emma', label: 'Emma', lang: 'en-gb', langLabel: 'Anglais UK', gender: 'F', flag: '🇬🇧' },
  { id: 'bf_isabella', label: 'Isabella', lang: 'en-gb', langLabel: 'Anglais UK', gender: 'F', flag: '🇬🇧' },
  { id: 'bf_lily', label: 'Lily', lang: 'en-gb', langLabel: 'Anglais UK', gender: 'F', flag: '🇬🇧' },
  { id: 'bm_daniel', label: 'Daniel', lang: 'en-gb', langLabel: 'Anglais UK', gender: 'M', flag: '🇬🇧' },
  { id: 'bm_fable', label: 'Fable', lang: 'en-gb', langLabel: 'Anglais UK', gender: 'M', flag: '🇬🇧' },
  { id: 'bm_george', label: 'George', lang: 'en-gb', langLabel: 'Anglais UK', gender: 'M', flag: '🇬🇧' },
  { id: 'bm_lewis', label: 'Lewis', lang: 'en-gb', langLabel: 'Anglais UK', gender: 'M', flag: '🇬🇧' },
  // Espagnol
  { id: 'ef_dora', label: 'Dora', lang: 'es', langLabel: 'Espagnol', gender: 'F', flag: '🇪🇸' },
  { id: 'em_alex', label: 'Alex', lang: 'es', langLabel: 'Espagnol', gender: 'M', flag: '🇪🇸' },
  { id: 'em_santa', label: 'Santa', lang: 'es', langLabel: 'Espagnol', gender: 'M', flag: '🇪🇸' },
  // Italien
  { id: 'if_sara', label: 'Sara', lang: 'it', langLabel: 'Italien', gender: 'F', flag: '🇮🇹' },
  { id: 'im_nicola', label: 'Nicola', lang: 'it', langLabel: 'Italien', gender: 'M', flag: '🇮🇹' },
  // Hindi
  { id: 'hf_alpha', label: 'Alpha', lang: 'hi', langLabel: 'Hindi', gender: 'F', flag: '🇮🇳' },
  { id: 'hf_beta', label: 'Beta', lang: 'hi', langLabel: 'Hindi', gender: 'F', flag: '🇮🇳' },
  { id: 'hm_omega', label: 'Omega', lang: 'hi', langLabel: 'Hindi', gender: 'M', flag: '🇮🇳' },
  { id: 'hm_psi', label: 'Psi', lang: 'hi', langLabel: 'Hindi', gender: 'M', flag: '🇮🇳' },
  // Portugais (BR)
  { id: 'pf_dora', label: 'Dora', lang: 'pt', langLabel: 'Portugais BR', gender: 'F', flag: '🇧🇷' },
  { id: 'pm_alex', label: 'Alex', lang: 'pt', langLabel: 'Portugais BR', gender: 'M', flag: '🇧🇷' },
  { id: 'pm_santa', label: 'Santa', lang: 'pt', langLabel: 'Portugais BR', gender: 'M', flag: '🇧🇷' },
  // Japonais
  { id: 'jf_alpha', label: 'Alpha', lang: 'ja', langLabel: 'Japonais', gender: 'F', flag: '🇯🇵' },
  { id: 'jf_gongitsune', label: 'Gongitsune', lang: 'ja', langLabel: 'Japonais', gender: 'F', flag: '🇯🇵' },
  { id: 'jf_nezumi', label: 'Nezumi', lang: 'ja', langLabel: 'Japonais', gender: 'F', flag: '🇯🇵' },
  { id: 'jf_tebukuro', label: 'Tebukuro', lang: 'ja', langLabel: 'Japonais', gender: 'F', flag: '🇯🇵' },
  { id: 'jm_kumo', label: 'Kumo', lang: 'ja', langLabel: 'Japonais', gender: 'M', flag: '🇯🇵' },
  // Chinois mandarin
  { id: 'zf_xiaobei', label: 'Xiaobei', lang: 'zh', langLabel: 'Chinois', gender: 'F', flag: '🇨🇳' },
  { id: 'zf_xiaoni', label: 'Xiaoni', lang: 'zh', langLabel: 'Chinois', gender: 'F', flag: '🇨🇳' },
  { id: 'zf_xiaoxiao', label: 'Xiaoxiao', lang: 'zh', langLabel: 'Chinois', gender: 'F', flag: '🇨🇳' },
  { id: 'zf_xiaoyi', label: 'Xiaoyi', lang: 'zh', langLabel: 'Chinois', gender: 'F', flag: '🇨🇳' },
  { id: 'zm_yunjian', label: 'Yunjian', lang: 'zh', langLabel: 'Chinois', gender: 'M', flag: '🇨🇳' },
  { id: 'zm_yunxi', label: 'Yunxi', lang: 'zh', langLabel: 'Chinois', gender: 'M', flag: '🇨🇳' },
  { id: 'zm_yunxia', label: 'Yunxia', lang: 'zh', langLabel: 'Chinois', gender: 'M', flag: '🇨🇳' },
  { id: 'zm_yunyang', label: 'Yunyang', lang: 'zh', langLabel: 'Chinois', gender: 'M', flag: '🇨🇳' },
];

/** Langues proposées (dédupliquées, dans l'ordre du catalogue). */
export const KOKORO_LANGUAGES: { code: string; label: string; flag: string }[] = (() => {
  const seen = new Set<string>();
  const out: { code: string; label: string; flag: string }[] = [];
  KOKORO_VOICES.forEach((v) => {
    if (seen.has(v.lang)) return;
    seen.add(v.lang);
    out.push({ code: v.lang, label: v.langLabel, flag: v.flag });
  });
  return out;
})();

/**
 * Voix adaptée à une langue de contenu.
 * Kokoro ne gère pas l'arabe ni la darija : on retombe sur le français
 * (meilleure prononciation des mots français présents dans la darija latine).
 */
export function voicesForLanguage(lang: string): KokoroVoice[] {
  const normalize = (code: string): string => {
    const c = (code || '').toLowerCase();
    if (c.startsWith('fr')) return 'fr';
    if (c === 'en-gb' || c === 'en_uk') return 'en-gb';
    if (c.startsWith('en')) return 'en';
    if (c.startsWith('es')) return 'es';
    if (c.startsWith('it')) return 'it';
    if (c.startsWith('hi')) return 'hi';
    if (c.startsWith('pt')) return 'pt';
    if (c.startsWith('ja')) return 'ja';
    if (c.startsWith('zh')) return 'zh';
    return 'fr';
  };
  const target = normalize(lang);
  const matching = KOKORO_VOICES.filter((v) => v.lang === target);
  return matching.length > 0 ? matching : KOKORO_VOICES.filter((v) => v.lang === 'fr');
}

/** Voix par défaut pour une langue donnée. */
export function defaultVoiceFor(lang: string): string {
  const list = voicesForLanguage(lang);
  return list[0]?.id || 'ff_siwis';
}

/** Vrai si Kokoro sait prononcer cette langue. */
export function isLanguageSupported(lang: string): boolean {
  const c = (lang || '').toLowerCase();
  if (c.startsWith('ar')) return false;
  return voicesForLanguage(lang).length > 0;
}

function cleanText(text: string): string {
  return (text || '')
    .replace(/\*\*/g, '')
    .replace(/[`_#>]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 4000);
}

export interface TtsResult {
  url: string;
  blob: Blob;
}

/** Petit cache mémoire : éviter de régénérer deux fois le même texte. */
const cache = new Map<string, string>();
const CACHE_LIMIT = 60;

/**
 * Génère un fichier audio MP3 à partir d'un texte.
 * Passe par le backend (`kokoro-tts`), jamais directement par l'IP Kokoro.
 */
export async function synthesizeSpeech(params: {
  text: string;
  voice?: string;
  speed?: number;
}): Promise<TtsResult> {
  const input = cleanText(params.text);
  if (!input) throw new Error('Aucun texte à lire.');

  const voice = params.voice || 'ff_siwis';
  const speed = Math.min(2, Math.max(0.5, params.speed ?? 1));
  const key = `${voice}|${speed}|${input}`;

  const cached = cache.get(key);
  if (cached) {
    const blob = await fetch(cached).then((r) => r.blob()).catch(() => null);
    if (blob) return { url: cached, blob };
  }

  const resp = await fetch(FUNCTIONS_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: ANON_KEY,
      Authorization: `Bearer ${ANON_KEY}`,
    },
    body: JSON.stringify({ text: input, voice, speed }),
  });

  if (!resp.ok) {
    let message = `La synthèse vocale a échoué (${resp.status}).`;
    try {
      const body = (await resp.json()) as { error?: string; message?: string };
      if (body?.message || body?.error) message = String(body.message || body.error);
    } catch {
      /* réponse non JSON : message générique conservé */
    }
    throw new Error(message);
  }

  const blob = await resp.blob();
  if (blob.size === 0) throw new Error('Le serveur vocal a renvoyé un fichier vide.');
  const url = URL.createObjectURL(blob);

  if (cache.size >= CACHE_LIMIT) {
    const oldest = cache.keys().next().value;
    if (oldest) {
      const oldUrl = cache.get(oldest);
      if (oldUrl) URL.revokeObjectURL(oldUrl);
      cache.delete(oldest);
    }
  }
  cache.set(key, url);

  return { url, blob };
}

/** Télécharge un blob audio sous forme de fichier MP3. */
export function downloadAudioBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.mp3') ? filename : `${filename}.mp3`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Enregistre l'audio généré dans la médiathèque du compte (Content Studio). */
export async function saveTtsToMedia(params: {
  ownerId: number;
  blob: Blob;
  name: string;
  createMedia: (input: { ownerId: number; kind: 'audio'; url: string; name: string }) => Promise<unknown>;
  upload: (blob: Blob, filename: string) => Promise<string>;
}): Promise<string> {
  const url = await params.upload(params.blob, `${params.name || 'voix'}.mp3`);
  await params.createMedia({ ownerId: params.ownerId, kind: 'audio', url, name: params.name });
  return url;
}

/** Indique si le backend TTS répond (utilisé par l'admin / les diagnostics). */
export async function checkTtsHealth(): Promise<{ ok: boolean; message: string; latencyMs?: number }> {
  const started = Date.now();
  try {
    const { data, error } = await supabase.functions.invoke('kokoro-tts', {
      body: { action: 'health' },
    });
    if (error) throw error;
    const payload = data as { ok?: boolean; message?: string } | null;
    return {
      ok: payload?.ok === true,
      message: payload?.message || (payload?.ok ? 'Serveur vocal joignable.' : 'Serveur vocal injoignable.'),
      latencyMs: Date.now() - started,
    };
  } catch (e) {
    return { ok: false, message: e instanceof Error ? e.message : 'Test impossible.' };
  }
}